import {useCallback, useMemo, useRef} from 'react';

const DURATION = 450;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * FLIP transitions for a grid whose layout changes when a card opens or closes:
 * cards that only move glide from their old to their new position, cards that change size grow in.
 *
 * Usage: `capture()` right before the state change, then `stop()` and `play()` in a layout effect.
 */
export function useCardTransitions() {
    const cards = useRef(new Map<string, HTMLElement>());
    const refs = useRef(new Map<string, (element: HTMLElement | null) => void>());
    const before = useRef<Map<string, DOMRect>>();

    /** A stable ref callback per card. */
    const register = useCallback((key: string) => {
        let ref = refs.current.get(key);
        if (!ref) {
            ref = element => {
                if (element) cards.current.set(key, element);
                else cards.current.delete(key);
            };
            refs.current.set(key, ref);
        }
        return ref;
    }, []);

    /** Remembers where every card is right now (mid-animation positions included). */
    const capture = useCallback(() => {
        before.current = new Map(Array.from(cards.current, ([key, element]) => [key, element.getBoundingClientRect()]));
    }, []);

    /** Cancels running transitions, so cards report their real layout position. */
    const stop = useCallback(() => {
        cards.current.forEach(element => element.getAnimations().forEach(animation => animation.cancel()));
    }, []);

    /** Animates from the captured positions to the current layout; `resized` are the cards that opened or closed. */
    const play = useCallback((resized: Set<string>) => {
        const first = before.current;
        before.current = undefined;
        if (!first || prefersReducedMotion()) return;

        cards.current.forEach((element, key) => {
            const from = first.get(key);
            if (!from) return;

            if (resized.has(key)) {
                element.animate(
                    [{opacity: 0.35, transform: 'scale(0.96)'}, {opacity: 1, transform: 'none'}],
                    {duration: DURATION, easing: EASING});
                return;
            }

            const to = element.getBoundingClientRect();
            const dx = from.left - to.left;
            const dy = from.top - to.top;
            if (dx || dy) {
                element.animate(
                    [{transform: `translate(${dx}px, ${dy}px)`}, {transform: 'none'}],
                    {duration: DURATION, easing: EASING});
            }
        });
    }, []);

    const element = useCallback((key: string) => cards.current.get(key), []);

    return useMemo(() => ({register, capture, stop, play, element}), [register, capture, stop, play, element]);
}
